import type { CSSProperties } from "react";
import { Icon, type IconName } from "@/components/common/Icon";
import { IconButton } from "@/components/common/IconButton";
import { SharedVolumeTreeRowFrame } from "@/components/volumes/SharedVolumeTreeRowFrame";
import {
	DEVICE_SHARED_VOLUME_CHANGE_LABELS,
	deviceSharedVolumeEntryChange,
	isDeviceSharedVolumeEntryMuted,
	type DeviceSharedVolumeEntryChange,
	type DeviceSharedVolumeEntryRow,
} from "@/lib/device-shared-volumes";
import type { EnvironmentEntryPayload } from "@/lib/dnd/payload";
import { BADGE_STYLE, COUNT_BADGE_STYLE, NEUTRAL_BADGE_STYLE } from "@/lib/pane-styles";
import { SHARED_VOLUME_STATE_LABELS } from "@/lib/shared-volumes";

const STATE_BADGE_STYLE: CSSProperties = { ...BADGE_STYLE, ...NEUTRAL_BADGE_STYLE };

/** 削除は名前の取り消し線で見せる (ゴミ箱のアイコンは押せるボタンに見えるため)。 */
type ChangeIconKind = Exclude<DeviceSharedVolumeEntryChange, "deleted">;

const CHANGE_ICONS: Readonly<Record<ChangeIconKind, { name: IconName; color: string }>> = {
	modified: { name: "pencil", color: "var(--warning)" },
	unsupported: { name: "alertCircle", color: "var(--fg-3)" },
};

function ChangeIcon({ kind }: { kind: ChangeIconKind }) {
	const { name, color } = CHANGE_ICONS[kind];
	return (
		<span
			className="flex shrink-0"
			data-tooltip={DEVICE_SHARED_VOLUME_CHANGE_LABELS[kind]}
			data-testid="shared-volume-node-change"
			data-change={kind}
		>
			<Icon name={name} size={12} color={color} />
		</span>
	);
}

const PUSH_LABEL = "共有ボリュームに反映";

/** 根の行の右端の、ボリューム全体を反映するボタン。 */
export interface DeviceSharedVolumeRootPush {
	disabled: boolean;
	/** 押せない理由。 */
	reason: string | null;
	onPush: () => void;
}

interface DeviceSharedVolumeTreeRowProps {
	row: DeviceSharedVolumeEntryRow;
	selected: boolean;
	/** Tab で入る行か。 */
	current: boolean;
	/** 直下を読んでいるか。 */
	loading: boolean;
	/** ボリュームの根で、そのボリュームの転送・一覧の取り直し・操作が進んでいるか。 */
	syncing: boolean;
	/** ボリュームの根で、反映のボタンを出すときだけ渡す。 */
	push: DeviceSharedVolumeRootPush | null;
	/** PC のファイルや ~ のものを落とせるか (ファイルの行は親に置く)。 */
	canDrop: boolean;
	onDropFiles: (row: DeviceSharedVolumeEntryRow, dataTransfer: DataTransfer) => void;
	onDropHomeEntries: (row: DeviceSharedVolumeEntryRow, payload: EnvironmentEntryPayload) => void;
	onClick: (row: DeviceSharedVolumeEntryRow, event: React.MouseEvent) => void;
	onContextMenu: (row: DeviceSharedVolumeEntryRow, event: React.MouseEvent) => void;
	onKeyDown: (
		row: DeviceSharedVolumeEntryRow,
		event: React.KeyboardEvent<HTMLButtonElement>,
	) => void;
}

/**
 * デバイスの環境のファイルのツリーの共有ボリュームの 1 行。ボリュームの根は名前の横に写しのパスを添え、取り込みの状態・
 * 手元の変更を印で出す。PC のファイルや ~ のものを落とすと手元の写しに置く。
 */
export function DeviceSharedVolumeTreeRow({
	row,
	selected,
	current,
	loading,
	syncing,
	push,
	canDrop,
	onDropFiles,
	onDropHomeEntries,
	onClick,
	onContextMenu,
	onKeyDown,
}: DeviceSharedVolumeTreeRowProps) {
	const { entry, volume } = row;
	const muted = entry !== null && isDeviceSharedVolumeEntryMuted(entry);
	const change = entry === null ? null : deviceSharedVolumeEntryChange(entry);
	const deleted = change === "deleted";
	const changedBelow =
		entry === null
			? volume.state.unpushedChangeCount
			: row.directory
				? entry.changedDescendants
				: 0;
	const volumeUnavailable = entry === null && volume.volumeState !== "ready";

	return (
		<SharedVolumeTreeRowFrame
			rowKey={row.key}
			name={row.name}
			depth={row.depth}
			directory={row.directory}
			expandable={row.expandable}
			expanded={row.expanded}
			mountPath={entry === null ? volume.mountPath : null}
			muted={muted}
			deleted={deleted}
			selected={selected}
			current={current}
			spinner={loading || syncing}
			spinnerTestId={syncing ? "shared-volumes-refreshing" : undefined}
			drop={{
				canDrop,
				onDropFiles: (dataTransfer) => onDropFiles(row, dataTransfer),
				onDropHomeEntries: (payload) => onDropHomeEntries(row, payload),
			}}
			onClick={(event) => onClick(row, event)}
			onContextMenu={(event) => onContextMenu(row, event)}
			onKeyDown={(event) => onKeyDown(row, event)}
			badges={
				<>
					{volumeUnavailable && (
						<span style={STATE_BADGE_STYLE}>{SHARED_VOLUME_STATE_LABELS[volume.volumeState]}</span>
					)}
					{change !== null && change !== "deleted" && <ChangeIcon kind={change} />}
					{changedBelow > 0 && (
						<span
							style={COUNT_BADGE_STYLE}
							data-tooltip={`反映していない変更が ${changedBelow} 件あります`}
							data-testid="shared-volume-node-changes"
						>
							{changedBelow}
						</span>
					)}
				</>
			}
			action={
				push && (
					<IconButton
						icon="databaseUpload"
						iconSize={12}
						size={18}
						className="shrink-0"
						disabled={push.disabled}
						data-tooltip={push.reason ?? PUSH_LABEL}
						// ツリーの Tab で入る行は 1 つにする。キーボードでは右クリックのメニューから反映する。
						tabIndex={-1}
						data-testid="shared-volume-root-push"
						onClick={(event) => {
							event.stopPropagation();
							push.onPush();
						}}
					/>
				)
			}
		/>
	);
}
